/**
 * Reveal requests for the Fusion canvas. The Evidence view opens with its group boxes folded into
 * one card each, so a node inside a folded box is not on the canvas. Anything that wants to point at
 * a node by id (the focus link, a cluster expand) calls `requestReveal(ids)` first; the canvas opens
 * the boxes that hold those ids, then the caller's existing "wait for the node to appear" polling
 * finds them. When no canvas is mounted yet (the caller is about to switch to it) the request is
 * kept for a few seconds and taken by the canvas that mounts next.
 */
type Listener = (ids: string[]) => void;
const listeners = new Set<Listener>();
const PENDING_TTL_MS = 10_000;
let pending: { ids: string[]; at: number } | null = null;

export function onReveal(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function requestReveal(ids: Iterable<string>): void {
  const list = [...ids].map(String).filter(Boolean);
  if (!list.length) return;
  if (listeners.size === 0) {
    pending = { ids: [...(pending && Date.now() - pending.at < PENDING_TTL_MS ? pending.ids : []), ...list], at: Date.now() };
    return;
  }
  listeners.forEach((l) => l(list));
}

/** Requests made while no canvas was mounted (and not older than the TTL); clears them. */
export function takePendingReveals(): string[] {
  const out = pending && Date.now() - pending.at < PENDING_TTL_MS ? pending.ids : [];
  pending = null;
  return out;
}
